import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import Container from '../../../../elements/Container'
import { URLS } from '../../../../_config'
import Dropdown, { Option } from '../../../../elements/Form/dropdown'
import toast from 'react-hot-toast'
import { useExperimentSelector } from '../../../../store/selectors/experimentSelector'
import { useExperimentApi } from '../../../../store/api/hooks/experimentApiHook'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { getColumnNamesAsOptions, getSelectedOption, makeUniqueOptionArray, trackEvent } from '../../../../_common/functions'
//import { EXPERIMENT_TYPES } from '../../../../_common/constant'
//import { useDialog } from '../../../../store/api/hooks/dialogHook'
import { useFpLoader } from '../../../../store/api/hooks/fpLoaderHook'
import { useMeasurementConfigure } from '../../../../_common/hook/useMeasurementConfigure'
import { BusinessFunctionDataType, columnDataTypes, DistributionColumnNameType } from '../../../../_config/types'
import { cellRenderer } from '../../../../components/cellRenderer'
import CustomAgGrid from '../../../../components/CustomAgGrid'
import { AgGridReact } from 'ag-grid-react'
import i18n from '../../../../i18n'
import MeasurementSuccessModal from '../modal/measurementSuccess.modal'
import Loader from '../../../../components/loader'
import { useCommonApi } from '../../../../store/api/hooks/commonApiHook'
import { ErrorResponse } from '../../../../store/api/apiTypes'
import CommonInputText from '../../../../components/commonInputText'
import Text from '../../../../elements/Typo/Text'
import { DateFrequency } from '..'

const DmlSection = ({ index, title, subtitle, children }: { index: number; title: string; subtitle: string; children: React.ReactNode }) => {
    return (
        <section className="rounded-[0.9rem] border border-[#dfe2e6] p-4">
            <div className="mb-4 flex items-start gap-3">
                <span className="grid h-6 w-6 place-items-center rounded-[0.35rem] bg-primary text-[0.75rem] font-bold text-secondary">{index}</span>
                <div>
                    <h3 className="m-0 text-[0.95rem] font-semibold text-[#111214]">{title}</h3>
                    <p className="m-0 mt-1 text-[0.68rem] leading-[1.25] text-[#646b76]">{subtitle}</p>
                </div>
            </div>
            {children}
        </section>
    )
}

const Label = ({ children }: { children: React.ReactNode }) => {
    return <label className="mb-1 block text-[0.68rem] font-semibold text-[#111214]">{children}</label>
}

const inputClassName = 'mt-1 min-h-[2.4rem] w-full rounded-[0.35rem] border border-[#c7cbd1] bg-white px-3 text-[0.75rem] text-[#22262b] outline-none focus:border-[#8a7d00] focus:shadow-[0_0_0_3px_rgba(255,230,0,0.2)]'

type SelectOption = {
    label: string
    value: string | string[]
}

export const getInteractionOptions = (fixedEffects: SelectOption[] = []): SelectOption[] =>
    fixedEffects.flatMap((first, i) =>
        fixedEffects.slice(i + 1).map((second) => ({
            label: `${first.label} x ${second.label}`,
            value: [String(first.value), String(second.value)],
        })),
    )

const treatmentTypeOptions: SelectOption[] = [
    { label: 'Binary', value: 'binary' },
    { label: 'Continuous exposure', value: 'continuous' },
]

const outcomeTransformOptions: SelectOption[] = [
    { label: 'Log — report percentage lift', value: 'log' },
    { label: 'Level — report unit lift', value: 'level' },
]

const HelperText = ({ children }: { children: React.ReactNode }) => {
    return <div className="mt-1 rounded-[0.35rem] border border-[#bee5d4] bg-[#eaf7f1] px-2 py-1 text-[0.65rem] font-semibold text-[#136846]">{children}</div>
}

const DMLMeasurementConfigurePage: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params: any = useParams()
    const experimentLiteral = params.experiment

    const [experiment, setExperiment] = useState<any>(null)
    const exp = useExperimentSelector()
    const experimentApi = useExperimentApi()
    const conmmonApi = useCommonApi()
    const [granularity, setGranularity] = useState<any>(null)
    const [dateCols, setDateCols] = useState<any>(null)
    const [columns, setColumns] = useState<any>(null)
    const [columnsData, setColumnsData] = useState<Record<columnDataTypes, string[]> | null>(null)
    const weeklyConfig: any = useRef(null)
    const [groupConfigsReady, setGroupConfigsReady] = useState<boolean>(false)
    const [working, setWorking] = useState<boolean>(false)
    const [kpiInterests, setKpiInterests] = useState<any>(null)
    //const dialog = useDialog()
    const [dataCalled, setDataCalled] = useState<boolean>(false)
    const { showLoader, hideLoader } = useFpLoader()
    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })
    const [columnDefs, setcolumnDefs] = useState<[]>([])
    const [rowData, setRowData] = useState<[]>([])
    const dataTableGrid = useRef<AgGridReact>(null)
    const [mdata, setMdata] = useState<any>(null)
    const measurementSuccessModalRef: any = useRef()

    const {
        control,
        getValues,
        handleSubmit,
        watch,
        trigger,
        setValue,
        formState: { errors },
        register,
    } = useForm<any>({
        defaultValues: {},
    })

    const selectedClusterColumns: SelectOption[] = watch('clusterColumns') ?? []
    const selectedCovariates: SelectOption[] = watch('covariates') ?? []
    const selectedInvestmentColumns: SelectOption[] = watch('investmentColumns') ?? []
    const selectedFixedEffects: SelectOption[] = watch('fixedEffects') ?? []
    const selectedInteractions: SelectOption[] = watch('interactions') ?? []
    const fixedEffectValues = selectedFixedEffects.map((effect) => effect.value).join('|')
    const interactionOptions = getInteractionOptions(selectedFixedEffects)

    // Drop interactions whose fixed effects were deselected.
    useEffect(() => {
        // Values are arrays, so compare by content rather than reference.
        const toKey = (value: SelectOption['value']) => JSON.stringify(value)
        const validValues = new Set(interactionOptions.map((option) => toKey(option.value)))
        const current: SelectOption[] = getValues('interactions') ?? []
        const kept = current.filter((option) => validValues.has(toKey(option.value)))
        if (kept.length !== current.length) {
            setValue('interactions', kept)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixedEffectValues])

    const renderDropdown = (name: string, options: Option[], { testId, isMulti = false, required = false }: { testId: string; isMulti?: boolean; required?: boolean }) => (
        <Controller
            name={name}
            control={control}
            rules={required ? { validate: (value: any) => (isMulti ? value?.length > 0 : !!value) || t('Please select') } : undefined}
            render={({ field: { value, onChange } }) => (
                <Dropdown
                    value={value}
                    isMulti={isMulti}
                    isClearable={isMulti}
                    onChange={(selected: any) => {
                        onChange(selected ?? (isMulti ? [] : null))
                        void trigger(name)
                        trackEvent('measurement_dml_configure_field_selected', { experiment_literal: experimentLiteral, field: name })
                    }}
                    options={options}
                    error={errors?.[name]?.message as string}
                    testId={testId}
                    className="mt-1 w-full"
                />
            )}
        />
    )

    const {
        fields: metricFields,
        append: metricAppend,
        remove: metricRemove,
    } = useFieldArray({
        control,
        name: 'metrics',
    })

    /* const {
        fields: groupConfigs,
        append: groupConfigAppend,
        update: groupConfigUpdate,
        remove: groupConfigsRemove,
    } = useFieldArray({
        control,
        name: 'groupConfigs',
    }) */

    const goBack = () => {
        void navigate(URLS.MEASUREMENT(experimentLiteral, 'filter'))
    }

    const runMeasurement = () => {
        const DATA: any = getValues()

        const payload: any = {
            literal: experimentLiteral,
            measurement_column_map: {
                granularity: DATA.clusterColumns.value,
                date: DATA.dateColumn.value,
                date_frequency: DATA.frequency,
                treatment: DATA.treatmentColumn.value,
                allocation_group: DATA.treatmentColumn.value,
                treatment_type: DATA.treatmentType.value,
                covariates: DATA.covariates.map((covariate: any) => covariate.value),
                kpi: DATA.metrics.map((kpi: any) => {
                    const kpidata = {
                        ...kpi,
                        column_map: kpi.column_map.value,
                    }
                    delete kpidata.id
                    return kpidata
                }),
                investments: DATA.investmentColumns.map((investment: any) => investment.value),
                fixed_effects: DATA.fixedEffects.map((effect: any) => effect.value),
                interactions: DATA.interactions?.map((interaction: any) => interaction.value),
                advance_config: {
                    outcome_transform: DATA.outcomeTransform.value,
                    include_lagged_outcome: DATA.includeLaggedOutcome,
                    auto_model: DATA.autoModel,
                    random_seed: Number(DATA.randomSeed),
                    cross_fitting: Number(DATA.crossFitting),
                },
            },
            submit_from: 'measurement_configuration',
        }

        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            () => {
                experimentApi.executedesignmergeoverview(
                    { literal: params.experiment },
                    (resp: any) => {
                        setMdata(resp)
                        measurementSuccessModalRef?.current?.showModal()
                        /* if (onContinue) {
                            onContinue()
                        } */
                    },
                    (error: any) => {},
                )
            },
            (error: any) => {
                toast.error(error?.message)
            },
        )
    }

    const prefillBlockingFactors = () => {
        const designBlockingFactorsOptions = experiment?.experiment_metadatas?.design_column_map?.blocking_factors
            .map((colName: string) => {
                return {
                    label: colName,
                    value: colName,
                }
            })
            .sort((a: any, b: any) => a.label.localeCompare(b.label))

        if (experiment?.experiment_metadatas?.measurement_column_map?.date_frequency == 'weekly') {
            const columnMap: any = experiment?.experiment_metadatas?.measurement_column_map
            weeklyConfig.current = {
                baseline_start_week: columnMap?.baseline_start_week,
                baseline_end_week: columnMap?.baseline_end_week,
                analysis_start_week: columnMap?.analysis_start_week,
                analysis_end_week: columnMap?.analysis_end_week,
            }
        }

        if (experiment?.experiment_metadatas?.measurement_column_map?.blocking_factors) {
            const blockingFactorsOptions = experiment?.experiment_metadatas?.measurement_column_map?.blocking_factors.map((colName: string) => {
                return {
                    label: colName,
                    value: colName,
                }
            })
            if (columns?.string_columns) {
                setTimeout(() => {
                    setValue('blockingFactors', blockingFactorsOptions)
                }, 500)
            }
        } else if (experiment?.experiment_metadatas?.design_column_map?.blocking_factors && !experiment?.is_measurement_flow) {
            setValue('blockingFactors', designBlockingFactorsOptions)
        }
    }

    const getColumnsResponseHandeler = (resp: any) => {
        return new Promise((resolve, reject) => {
            setColumnsData(resp.data)
            let allColumns: any[] = []
            for (const arr of Object.values(resp.data)) {
                allColumns = [...allColumns, ...(arr as any)]
            }
            const uarr: any[] = []
            for (const str of allColumns) {
                if (!uarr.includes(str)) {
                    uarr.push(str)
                }
            }
            const _columns: any = {}
            const missingValuesCols: string[] = resp.data.missing_values_column ?? []
            resp.data['datetime_columns'] = (resp.data['datetime_columns'] ?? []).filter((col: string) => !missingValuesCols.includes(col))

            for (const key of Object.keys(resp.data)) {
                _columns[key as DistributionColumnNameType] = resp.data[key].map((name: any) => {
                    return {
                        label: name,
                        value: name,
                    }
                })
            }

            setDateCols([..._columns.datetime_columns])

            const options = [..._columns.boolean_columns, ..._columns.numeric_columns, ..._columns.string_columns, ..._columns.unique_columns]
            setGranularity(makeUniqueOptionArray(options))

            _columns.all = uarr.map((name: any) => {
                return {
                    label: name,
                    value: name,
                }
            })
            setColumns(_columns)
            resolve(_columns)
        })
    }

    const getColumnSuccess = (resp: any, resolve: any, reject: any) => {
        getColumnsResponseHandeler(resp)
            .then((_columns: any) => {
                resolve(_columns)
            })
            .catch((error: any) => {
                reject(new Error(error?.message || 'An unknown error occurred'))
            })
    }

    const getColumns = () => {
        return new Promise((resolve, reject) => {
            experimentApi.getColumnsForGroupDristribution(
                {
                    literal: experiment.experiment_literal,
                    tableType: 'KPI',
                },
                (resp: any) => {
                    getColumnSuccess(resp, resolve, reject)
                },
                (error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                },
            )
        })
    }

    const createColumnDes = (data: any) => {
        return data.map((key: any, i: number) => {
            return {
                headerName: key,
                field: key,
                filter: false,
                sortable: false,
                suppressMenu: true,
                cellStyle: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
                cellRenderer,
            }
        })
    }

    const getTableData = (datacountry: any[] = []) => {
        return new Promise((resolve, reject) => {
            measurementHookFunction
                .getTableData(experiment)
                .then((data: any) => {
                    const columnDefs: any = createColumnDes(Object.keys(data[0]))
                    setcolumnDefs(columnDefs)
                    setRowData(data)
                    resolve(data)
                })
                .catch((error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                })
        })
    }

    /* const GetDistributionData = () => {
        experimentApi.GetMeasurementGroupDistributionData(
            { literal: experiment?.experiment_literal },
            (resp: any) => {
                setWorking(false)
                if (resp.data.groups && resp.data.total_size) {
                    if (experiment?.experiment_type?.title === EXPERIMENT_TYPES.OBS.title) {
                        //OSTUDY_GroupValidation(resp)
                    } else {
                        //RCT_GroupValidation(resp)
                    }
                } else {
                    console.log('did not get proper response from API')
                }
            },
            (error: any) => {
                setWorking(false)
                dialog.confirm({
                    type: 'delete',
                    title: t('ERROR'),
                    message: error?.message,
                    confirmButtonLabel: t('Ok'),
                    cancelButton: false,
                    onConfirm: () => { },
                })
                //toast.error(error?.message)
            },
        )
    } */

    const getKpiTypes = () => {
        conmmonApi.getKpiTypes(
            { sort_by: '-created_at', page: 1, per_page: 10 },
            (data: any) => {
                const interests = structuredClone(data.data.result)?.map((kpi: any) => {
                    kpi.label = kpi.name
                    kpi.value = kpi.kpi_literal
                    return kpi
                })
                setKpiInterests(interests)
            },
            (error: ErrorResponse<BusinessFunctionDataType>) => {
                toast.error(error.message)
            },
        )
    }

    useEffect(() => {
        if (experiment?.experiment_metadatas?.metrices && columns) {
            metricRemove()

            const Options: any = [...(columns?.numeric_columns || []), ...(columns?.boolean_columns || [])]

            if (experiment?.experiment_metadatas?.measurement_column_map?.kpi && experiment?.experiment_metadatas?.measurement_column_map?.kpi.length > 0) {
                experiment?.experiment_metadatas?.measurement_column_map.kpi.map((kpi: any) => {
                    metricAppend({ ...kpi, column_map: getSelectedOption(kpi.column_map, Options) })
                })
            } else {
                experiment?.experiment_metadatas?.metrices.map((kpi: any) => {
                    metricAppend(kpi)
                })
            }
        }

        if (experiment?.experiment_metadatas?.measurement_column_map?.kpi && experiment?.experiment_metadatas?.group_configs?.length > 0 && !groupConfigsReady && experiment?.is_measurement_flow) {
            setGroupConfigsReady(true)
            //GetDistributionData()
        }

        if (!dataCalled && experiment) {
            if (experiment?.data_mappings?.delta_measurement_table_name || experiment?.data_mappings?.user_created_measurement_table_name) {
                showLoader()
                setDataCalled(true)
                Promise.all([getColumns(), getTableData()])
                    .then((values: any) => {
                        setTimeout(hideLoader, 1200)
                    })
                    .catch((error: any) => {
                        hideLoader()
                        toast.error(error?.message || t('Error fetching measurement data'))
                    })
            }
        }
    }, [experiment, columns])

    useEffect(() => {
        if (granularity && experiment && dateCols && columns) {
            console.log('prefill form values - xxxxxxxxxxxx -', experiment?.experiment_metadatas?.measurement_column_map)
            if (experiment?.experiment_metadatas?.measurement_column_map?.granularity) {
                setValue('_name1', getSelectedOption(experiment?.experiment_metadatas?.measurement_column_map?.granularity, granularity))
                setValue('_name3', getSelectedOption(experiment?.experiment_metadatas?.measurement_column_map?.allocation_group, columns ? columns.string_columns : []))
                setValue('_name2', getSelectedOption(experiment?.experiment_metadatas?.measurement_column_map?.date, dateCols || []))
                setValue('frequency', experiment?.experiment_metadatas?.measurement_column_map?.date_frequency)

                //setValue('blockingFactors', matchedBlockingFactors);
            }
            prefillBlockingFactors()
        }
    }, [granularity, experiment, dateCols, columns])

    useEffect(() => {
        if (exp?.experiment_literal == params?.experiment) {
            setExperiment(exp)
        }
    }, [exp])

    useEffect(() => {
        getKpiTypes()
    }, [])

    return (
        <Container size="md">
            <div className="shadow-1 rounded-[0.833rem] bg-white p-5">
                <form onSubmit={handleSubmit(runMeasurement)} noValidate data-testid="dml-configure-form">
                    <section className="rounded-[1rem] border border-[#e5e7ea] p-5 shadow-[0_10px_28px_rgba(22,24,29,0.08)]">
                        <div className="mb-4 flex items-center justify-between gap-3">
                            <div>
                                <h2 className="m-0 text-[1.1rem] font-semibold text-[#111214]">DML – Watchtower Media Impact</h2>
                                <p className="m-0 mt-1 text-[0.72rem] text-[#646b76]">Map the panel structure and causal adjustment variables</p>
                            </div>
                        </div>

                        <DmlSection index={1} title="Panel structure" subtitle="Tell DML how units repeat over time and which continuous KPI to model.">
                            <div className="grid gap-4 md:grid-cols-3">
                                <div>
                                    <Label>Granularity *</Label>
                                    {renderDropdown('clusterColumns', getColumnNamesAsOptions(columnsData, ['string_columns']), { testId: 'unit-cluster-columns', isMulti: false, required: true })}
                                    <HelperText>{`✓ ${selectedClusterColumns.length} cluster column${selectedClusterColumns.length === 1 ? '' : 's'} selected`}</HelperText>
                                </div>
                                <div>
                                    <Label>Date Column *</Label>
                                    {renderDropdown('dateColumn', getColumnNamesAsOptions(columnsData, ['datetime_columns']), { testId: 'date-column', required: true })}
                                    {/* <HelperText>✓ Weekly and balanced</HelperText> */}
                                    <div className="bg-[#FAF8EF] border border-[#0AD4E133] rounded-[0.45rem] mt-2" data-testid="validation-date">
                                        <div className="flex justify-between p-2">
                                            <Text size="lg" className="font-semibold">
                                                {t('Validation:')} {t('No Missing Values')}
                                            </Text>
                                        </div>
                                        <div className="border-b border-[#0AD4E133]"></div>
                                        <div className="flex items-center justify-between p-1">
                                            <Text size="md" className="!leading-none">
                                                {t('Select Time Granularity')}
                                            </Text>
                                            {DateFrequency(control, trigger, metricFields, t)}
                                        </div>
                                        <div className="flex justify-end" data-testid="frequency-error-message">
                                            {errors?.frequency?.message ? <p className="text-error text-[.542rem] mt-1">{errors?.frequency?.message as string}</p> : null}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Label>Outcome / KPI Column *</Label>

                                    <div>
                                        {kpiInterests && (
                                            <>
                                                {metricFields.map((kpi: any, i: number) => (
                                                    <div key={`kpi-${i.toString()}`} className="grid grid-cols-2 gap-2 mb-2" data-testid={`kpi-row-${i}`}>
                                                        <CommonInputText value={getSelectedOption(kpi.kpi_literal, kpiInterests)?.label} disabled={true} data-testid={`kpi-label-${i}`} />
                                                        <Controller
                                                            name={`metrics.${i}.column_map`}
                                                            control={control}
                                                            rules={{
                                                                required: t('Please select'),
                                                            }}
                                                            render={({ field: { value, onChange } }) => (
                                                                <Dropdown
                                                                    value={value}
                                                                    onChange={(value: any) => {
                                                                        onChange(value)
                                                                        void trigger(`metrics.${i}`)
                                                                        trackEvent('measurement_mte_configure_kpi_mapped', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                                                    }}
                                                                    options={getColumnNamesAsOptions(columnsData, ['numeric_columns'])}
                                                                    error={(errors?.metrics as any)?.[i]?.column_map?.message as string}
                                                                    data-testid={`kpi-column-dropdown-${i}`}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </DmlSection>

                        <div className="h-4" />

                        <DmlSection index={2} title="Intervention" subtitle="Map Watchtower exposure. This is distinct from the continuous outcome KPI.">
                            <div className="grid gap-4 md:grid-cols-2">
                                <div>
                                    <Label>Treatment Column *</Label>
                                    {renderDropdown('treatmentColumn', getColumnNamesAsOptions(columnsData, ['numeric_columns', 'boolean_columns']), { testId: 'treatment-column', required: true })}
                                    <HelperText>✓ Values match selected treatment type</HelperText>
                                </div>
                                <div>
                                    <Label>Treatment Type *</Label>
                                    {renderDropdown('treatmentType', treatmentTypeOptions, { testId: 'treatment-type', required: true })}
                                    <p className="mt-1 text-[0.65rem] text-[#646b76]">Binary: intervention vs BAU. Continuous: treatment intensity.</p>
                                </div>
                            </div>
                        </DmlSection>

                        <div className="h-4" />

                        <DmlSection
                            index={3}
                            title="Causal adjustments"
                            subtitle="Choose background factors that may influence the KPI. Keep investment activity separate so DML does not mistake part of the intervention for a normal background factor."
                        >
                            <div className="grid gap-4">
                                <div>
                                    <Label>Covariates *</Label>
                                    {renderDropdown('covariates', getColumnNamesAsOptions(columnsData, ['numeric_columns', 'string_columns', 'boolean_columns']), { testId: 'covariates', isMulti: true, required: true })}
                                    <HelperText>{`✓ ${selectedCovariates.length} pre-treatment covariate${selectedCovariates.length === 1 ? '' : 's'} selected · no investment overlap`}</HelperText>
                                </div>

                                <div>
                                    <Label>Investment / Lever Columns</Label>
                                    {renderDropdown('investmentColumns', getColumnNamesAsOptions(columnsData, ['numeric_columns', 'string_columns']), { testId: 'investment-columns', isMulti: true })}
                                    <HelperText>{`✓ ${selectedInvestmentColumns.length} investment / lever column${selectedInvestmentColumns.length === 1 ? '' : 's'} selected`}</HelperText>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <Label>Fixed Effects</Label>
                                        {renderDropdown('fixedEffects', getColumnNamesAsOptions(columnsData, ['numeric_columns', 'string_columns']), { testId: 'fixed-effects', isMulti: true })}
                                        <HelperText>{`✓ ${selectedFixedEffects.length} fixed effect${selectedFixedEffects.length === 1 ? '' : 's'} selected`}</HelperText>
                                    </div>
                                    <div>
                                        <Label>Interactions</Label>
                                        {renderDropdown('interactions', interactionOptions, { testId: 'interactions', isMulti: true })}
                                        <HelperText>{`✓ ${selectedInteractions.length} interaction${selectedInteractions.length === 1 ? '' : 's'} selected`}</HelperText>
                                    </div>
                                </div>
                            </div>
                        </DmlSection>

                        <div className="h-4" />

                        <details className="rounded-[0.9rem] border border-[#dfe2e6] bg-[#fafafa]" open>
                            <summary className="cursor-pointer list-none px-4 py-3 text-[0.9rem] font-semibold text-[#111214] marker:hidden">
                                ⚙ Advanced Configuration <span className="ml-2 text-[0.65rem] font-normal text-[#646b76]">Recommended defaults are prefilled</span>
                            </summary>
                            <div className="px-4 pb-4 pt-0">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <Label>Outcome Transform</Label>
                                        {renderDropdown('outcomeTransform', outcomeTransformOptions, { testId: 'outcome-transform' })}
                                        <p className="mt-1 text-[0.65rem] text-[#646b76]">Defaults to Log.</p>
                                    </div>
                                    <div>
                                        <Label>Cross-fitting Splits (n_splits)</Label>
                                        <input className={inputClassName} type="number" min={2} max="10" defaultValue={2} aria-label="Cross-fitting Splits" {...register('crossFitting')} />
                                    </div>
                                    <div>
                                        <Label>Random Seed</Label>
                                        <input className={inputClassName} type="number" min={42} max="100" defaultValue={42} aria-label="Random Seed" {...register('randomSeed')} />
                                    </div>
                                    <div>
                                        <Label>Include Lagged Outcome</Label>
                                        <div className="mt-1 flex min-h-[2.4rem] items-center justify-between rounded-[0.35rem] border border-[#c7cbd1] bg-white px-3">
                                            <span className="text-[0.7rem] text-[#111214]">Use previous-period KPI</span>
                                            <input type="checkbox" defaultChecked className="h-4 w-4 accent-[#ffe600]" aria-label="Include Lagged Outcome" {...register('includeLaggedOutcome')} />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Auto Model Selection</Label>
                                        <div className="mt-1 flex min-h-[2.4rem] items-center justify-between rounded-[0.35rem] border border-[#c7cbd1] bg-white px-3">
                                            <span className="text-[0.7rem] text-[#111214]">Run outcome-model tournament</span>
                                            <input type="checkbox" className="h-4 w-4 accent-[#ffe600]" aria-label="Auto Model Selection" {...register('autoModel')} />
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-3 text-[0.65rem] leading-[1.4] text-[#646b76]">
                                    Inference safeguards (positivity threshold, SE routing, bootstrap and placebo counts) use governed system defaults and are intentionally not exposed in the standard UI.
                                </p>
                            </div>
                        </details>

                        <p className="text-[1rem] font-bold mt-4" data-testid="header-kpi-data">
                            {t('KPI data')}
                        </p>

                        <div className="mt-1 mb-4">
                            <CustomAgGrid
                                key={'mte1_' + i18n.language}
                                gridRef={dataTableGrid}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={{ sortable: true, filter: true }}
                                theme="light"
                                pagination={false}
                                paginationPageSize={5}
                                paginationPageSizeSelector={[5, 10, 20, 50]}
                                style={{ height: 350, width: '100%' }}
                                gridId="summmyGrid"
                                rowHeight={50}
                                alternativeRowColor={'#E5B61112'}
                                data-testid="kpi-custom-ag-grid"
                            />
                        </div>

                        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <button type="button" onClick={goBack} className="min-h-[2.4rem] rounded-[0.4rem] border border-[#111214] bg-white px-4 text-[0.78rem] font-semibold text-[#111214]">
                                {t('Previous Step')}
                            </button>
                            <button type="submit" disabled={working} className="min-h-[2.4rem] rounded-[0.4rem] border border-[#111214] bg-[#111214] px-4 text-[0.78rem] font-semibold text-white">
                                {working ? <Loader data-testid="loader-working" /> : t('Run Measurement')}
                            </button>
                        </div>
                    </section>
                </form>
                <MeasurementSuccessModal _ref={measurementSuccessModalRef} mdata={mdata} data-testid="measurement-success-modal" updateExperiment={true} />
            </div>
        </Container>
    )
}

export default DMLMeasurementConfigurePage
